// Design: Premium dark theme, teal #00D4AA accent, amber highlights
// Consistent with existing service pages: hero, what-we-cover grid, how-it-works steps,
// package cards, FAQ accordion, CTA strip.
// Schema: WebPage + Service + FAQPage + BreadcrumbList JSON-LD

import { Link } from "wouter";
import FaqAccordion from "@/components/FaqAccordion";
import ArabicToggle from "@/components/ArabicToggle";
import AudioPlayer from "@/components/AudioPlayer";
import {
  ArrowRight,
  Brain,
  Zap,
  Users,
  TrendingUp,
  MessageSquare,
  Clock,
  CheckCircle2,
  ChevronDown,
  Lightbulb,
  Target,
} from "lucide-react";
import Header from "@/components/Header";
import SEOHead from "@/components/SEOHead";
import Footer from "@/components/Footer";
import ScoreInlineForm from "@/components/ScoreInlineForm";
import SharePostRow from "@/components/SharePostRow";


import { trackSectionCtaClick, trackCalendlyClick } from "@/lib/analytics";

import SchemaScript from "@/components/SchemaScript";
const AI_TRAINING_HERO = {
  jpeg: "/media/ai-training-leaders-service-hero-1600_e8f2d17e.jpg",
  jpegSrcSet: [
    "/media/ai-training-leaders-service-hero-640_e92fb115.jpg 640w",
    "/media/ai-training-leaders-service-hero-960_06d66f7a.jpg 960w",
    "/media/ai-training-leaders-service-hero-1280_cdc628a7.jpg 1280w",
  ].join(", "),
  webpSrcSet: [
    "/media/ai-training-leaders-service-hero-640_e4c9c27d.webp 640w",
    "/media/ai-training-leaders-service-hero-960_59fa1581.webp 960w",
    "/media/ai-training-leaders-service-hero-1280_7aa3391d.webp 1280w",
  ].join(", "),
};
const AI_TRAINING_HERO_OG = `https://integrated.social${AI_TRAINING_HERO.jpeg}`;

const faqs = [
  {
    q: "Do I need a technical background to attend?",
    a: "No. These sessions are designed specifically for business leaders, managers, and professionals who use AI tools in their daily work but have no coding or technical background. We use plain language, real business examples, and hands-on exercises. If you can write an email, you can learn to prompt AI effectively.",
  },
  {
    q: "What is prompt engineering and why does it matter for my business?",
    a: "Prompt engineering is the skill of writing clear, structured instructions that get AI tools like ChatGPT, Claude, or Gemini to produce useful, accurate, and on-brand outputs. Most people get mediocre results from AI because they treat it like a search engine. Professionals who know how to prompt correctly get outputs that save hours, improve quality, and compound over time. It is the single highest-leverage skill in the AI era for non-technical professionals.",
  },
  {
    q: "How is this different from watching YouTube tutorials?",
    a: "YouTube tutorials are generic. These sessions are built around your specific role, your industry, and the actual tasks you do every day. We work through your real use cases live, so you leave with prompts and workflows you can use immediately, not theory you have to translate later. You also get direct feedback on your prompts in real time, which accelerates learning dramatically.",
  },
  {
    q: "Will AI take my job?",
    a: "AI is not taking your job. People who know how to use AI are taking your job. The professionals who thrive in the next five years are not the ones who resist AI, they are the ones who learn to direct it, verify it, and apply it to real business problems faster than their peers. This training gives you that edge in three focused sessions.",
  },
  {
    q: "What AI tools do you cover?",
    a: "We cover the tools most relevant to your role: ChatGPT, Claude, Gemini, Perplexity, and Copilot for Microsoft 365. We also cover how to choose the right tool for each task, how to evaluate AI outputs critically, and how to build repeatable prompt templates your whole team can use.",
  },
  {
    q: "Can you run this for my whole team or leadership group?",
    a: "Yes. The Leadership cohort is designed for groups of 4 to 12 executives or senior managers. We run it as a private workshop series, tailored to your industry and business context. Team training creates a shared vocabulary and consistent AI practices across your organization, which compounds the productivity gains.",
  },
  {
    q: "What do I get after the three sessions?",
    a: "You receive a personal prompt library of 20 to 30 tested prompts for your specific role, a workflow map showing where AI fits into your daily tasks, a cheat sheet of the most effective prompt structures, and 30 days of email support for follow-up questions. Leaders on the Authority package also receive a recorded session library and a team prompt playbook.",
  },
  {
    q: "How quickly will I see results?",
    a: "Most participants report saving 1 to 3 hours per day within the first week of applying what they learn. The compounding effect is significant: professionals who build strong AI habits in the first 30 days typically reach 3 to 5x productivity gains within 90 days compared to colleagues who have not been trained.",
  },
  {
    q: "Which package is for a founder, and which is for a leadership team?",
    a: "Starter is one 90-minute session at £495, for a person who needs the foundations on their own work. Accelerator is the three-session course at £1,200 a person. Authority is the cohort, from £3,500 for 4 to 12 people, and it is the tier that includes direct access to Modi. A team that only needs a shared vocabulary should not be sold three separate Starter seats if Authority is the fit, and a single founder should not be pushed into a cohort price.",
  },
];

export default function ServiceAITraining() {

  const schemaData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "WebPage",
        "@id": "https://integrated.social/services/ai-training-for-leaders/",
        url: "https://integrated.social/services/ai-training-for-leaders/",
        name: "AI Training for Business Leaders and Non-Techies | Integrated.Social",
        description:
          "Three-session AI crash course for B2B leaders, fintech professionals, and non-technical teams. Learn to prompt AI tools for daily business use, boost productivity 3 to 5x, and stay ahead of colleagues who are already using AI.",
        isPartOf: { "@id": "https://integrated.social/#website" },
        about: { "@id": "https://integrated.social/services/ai-training-for-leaders/#service" },
        author: { "@id": "https://integrated.social/modi-elnadi/#person" },
        inLanguage: "en-GB",
      },
      {
        "@type": "Service",
        "@id": "https://integrated.social/services/ai-training-for-leaders/#service",
        name: "AI Training for Business Leaders and Non-Techies",
        provider: { "@id": "https://integrated.social/#organization" },
        serviceType: "AI Productivity Training and Prompt Engineering",
        description:
          "Three-session AI crash course for B2B leaders, fintech professionals, and non-technical teams. Learn to prompt AI tools for daily business use and boost productivity 3 to 5x.",
        areaServed: [
          { "@type": "Country", name: "United Kingdom" },
          { "@type": "Country", name: "United States" },
          { "@type": "Place", name: "Global" },
        ],
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "AI Training Packages",
          itemListElement: [
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Starter: AI Foundations for Professionals (1 session)",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Accelerator: 3-Session AI Crash Course for Non-Techies",
              },
            },
            {
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: "Authority: AI Leadership Program for Executives and Teams",
              },
            },
          ],
        },
      },
      {
        "@type": "FAQPage",
        "@id": "https://integrated.social/services/ai-training-for-leaders/#faq",
        mainEntity: faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        "@id": "https://integrated.social/services/ai-training-for-leaders/#breadcrumb",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://integrated.social/",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Services",
            item: "https://integrated.social/services/",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "AI Training for Leaders",
            item: "https://integrated.social/services/ai-training-for-leaders/",
          },
        ],
      },
    ],
  };

  const packages = [
    {
      name: "Starter",
      tagline: "AI Foundations",
      price: "£495",
      unit: "per person",
      sessions: "1 session · 90 minutes",
      color: "border-[#00D4AA]/30 bg-[#00D4AA]/5",
      accent: "text-[#00D4AA]",
      badge: null,
      includes: [
        "How AI tools actually work (plain English)",
        "The 5 prompt structures that cover 80% of business tasks",
        "Live exercises using your real work examples",
        "Personal prompt starter kit (10 ready-to-use prompts)",
        "30-day email Q&A support",
      ],
      cta: "Book Starter Session",
      href: "https://calendly.com/modielnadi/30min?utm_source=website&utm_medium=service&utm_campaign=ai-training-starter",
    },
    {
      name: "Accelerator",
      tagline: "3-Session Crash Course",
      price: "£1,200",
      unit: "per person",
      sessions: "3 sessions · 90 minutes each",
      color: "border-[#FF8C00]/40 bg-[#FF8C00]/5",
      accent: "text-[#FF8C00]",
      badge: "Most Popular",
      includes: [
        "Everything in Starter",
        "Advanced prompting: multi-step reasoning, role-play, chain-of-thought",
        "AI for your specific role: sales, marketing, ops, finance, leadership",
        "Building reusable prompt templates for your team",
        "How to verify AI outputs and avoid costly mistakes",
        "Personal prompt library (25 tested prompts for your role)",
        "Workflow map: where AI fits into your daily tasks",
        "60-day email Q&A support",
      ],
      cta: "Book Accelerator",
      href: "https://calendly.com/modielnadi/30min?utm_source=website&utm_medium=service&utm_campaign=ai-training-accelerator",
    },
    {
      name: "Authority",
      tagline: "AI Leadership Program",
      price: "From £3,500",
      unit: "per cohort (4–12 people)",
      sessions: "3 sessions + team workshop",
      color: "border-white/10 bg-white/5",
      accent: "text-white",
      badge: "For Teams",
      includes: [
        "Everything in Accelerator",
        "Private cohort for your leadership team or department",
        "Industry-specific use cases (fintech, SaaS, professional services)",
        "AI policy and governance basics for leaders",
        "Team prompt playbook your whole organization can use",
        "Recorded session library for onboarding new team members",
        "90-day follow-up workshop to review progress",
        "Direct access to Modi for strategic AI questions",
      ],
      cta: "Book a Discovery Call",
      href: "https://calendly.com/modielnadi/30min?utm_source=website&utm_medium=service&utm_campaign=ai-training-authority",
    },
  ];

  return (
    <>
      <SEOHead
        title="AI Training for Business Leaders London | Executive AI Crash Course | Integrated.Social"
        description="Three-session AI crash course for executives and non-technical leaders. Learn to use ChatGPT, Claude, and Gemini for real business decisions. 3-5x productivity gain in 90 days. London and remote."
        canonical="https://integrated.social/services/ai-training-for-leaders/"
        ogImage={AI_TRAINING_HERO_OG}
      />
      <SchemaScript schema={JSON.stringify(schemaData)} />
      <div className="min-h-screen bg-background">
        <Header />
        <main>

        {/* Breadcrumb */}
        <nav className="container pt-24 pb-4" aria-label="Breadcrumb">
          <ol className="flex items-center gap-2 text-sm text-muted-foreground flex-wrap">
            <li>
              <Link href="/" className="hover:text-foreground transition-colors">
                Home
              </Link>
            </li>
            <li>/</li>
            <li>
              <Link href="/#services" className="hover:text-foreground transition-colors">
                Services
              </Link>
            </li>
            <li>/</li>
            <li className="text-foreground font-medium">AI Training for Leaders</li>
          </ol>
        </nav>

        {/* Hero */}
        <header className="container pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="max-w-4xl">
              <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest text-[#00D4AA] border border-[#00D4AA]/30 rounded-full mb-6">
                For Leaders &amp; Non-Techies
              </span>
              <h1 className="font-serif headline-xl mb-6">
                AI is not taking your job.<br />
                <span className="text-[#FF8C00]">People who use AI are.</span>
              </h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl leading-relaxed mb-4">
                A three-session crash course that puts B2B leaders, fintech professionals, and
                non-technical teams on the right side of the AI divide. No code. No jargon.
                Just the skills that compound into a 3 to 5x productivity advantage.
              </p>
              <p className="text-base text-muted-foreground max-w-2xl leading-relaxed mb-8">
                You do not need to become a developer. You need to learn how to direct AI the way
                a great manager directs a team: clearly, precisely, and with the right expectations.
                That skill takes three sessions to learn and a career to benefit from.
              </p>
              <div className="flex flex-wrap gap-4">
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                  href="https://calendly.com/modielnadi/30min?utm_source=website&utm_medium=service&utm_campaign=ai-training-hero"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_ai_training"); trackSectionCtaClick({ section_name: "service_ai_training_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
                >
                  Book your first session <ArrowRight className="w-4 h-4" />
                </a>
                <a
                  href="#packages"
                  className="inline-flex items-center gap-2 px-6 py-3 border border-border text-foreground font-medium rounded-lg hover:bg-accent transition-all duration-200"
                >
                  See packages &amp; pricing
                </a>
              </div>
            </div>
            <div className="hidden lg:flex flex-col gap-4">
              <figure className="overflow-hidden rounded-xl border border-[#00D4AA]/20 bg-[#00D4AA]/5">
                <picture>
                  <source
                    type="image/webp"
                    srcSet={AI_TRAINING_HERO.webpSrcSet}
                    sizes="(max-width: 1279px) 100vw, 50vw"
                  />
                  <source
                    type="image/jpeg"
                    srcSet={AI_TRAINING_HERO.jpegSrcSet}
                    sizes="(max-width: 1279px) 100vw, 50vw"
                  />
                  <img
                    src={AI_TRAINING_HERO.jpeg}
                    alt="Business leaders collaborating with an AI workflow board during a practical training session"
                    width={1600}
                    height={900}
                    sizes="(max-width: 1279px) 100vw, 50vw"
                    loading="lazy"
                    decoding="async"
                    className="aspect-video w-full object-cover"
                  />
                </picture>
              </figure>
              <div className="grid grid-cols-2 gap-4">
                {[
                  { icon: Brain, label: "No Technical Background Needed", color: "text-[#00D4AA]", bg: "bg-[#00D4AA]/10 border-[#00D4AA]/20" },
                  { icon: Zap, label: "3 to 5x Productivity Gain", color: "text-[#FF8C00]", bg: "bg-[#FF8C00]/10 border-[#FF8C00]/20" },
                  { icon: Clock, label: "3 Sessions, 90 Min Each", color: "text-[#00D4AA]", bg: "bg-[#00D4AA]/10 border-[#00D4AA]/20" },
                  { icon: Users, label: "1-to-1 or Team Cohorts", color: "text-[#FF8C00]", bg: "bg-[#FF8C00]/10 border-[#FF8C00]/20" },
                ].map((item, i) => (
                  <div
                    key={i}
                    className={`border rounded-xl p-5 flex flex-col items-center text-center gap-3 ${item.bg}`}
                  >
                    <item.icon className={`w-8 h-8 ${item.color}`} />
                    <span className="text-sm font-semibold text-foreground">{item.label}</span>
                  </div>
                ))}
              </div>
              <div className="border border-[#FF8C00]/20 bg-[#FF8C00]/5 rounded-xl p-5 text-center">
                <p className="text-sm text-muted-foreground">
                  Covering{" "}
                  <span className="font-semibold text-foreground">ChatGPT · Claude · Gemini · Copilot · Perplexity</span>
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* The Real Problem */}
        <section className="bg-[#1a1a1a] text-white py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-4">The gap is already opening</h2>
            <p className="text-gray-400 text-lg max-w-2xl mb-12">
              In every organization, a small group of professionals is quietly getting 3 to 5x more
              done using AI. They are not smarter. They are not more senior. They learned one skill
              their colleagues have not: how to prompt.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: TrendingUp,
                  title: "The productivity gap is compounding",
                  desc: "Professionals who build AI habits in the first 30 days reach 3 to 5x output within 90 days. The gap between trained and untrained colleagues widens every week.",
                },
                {
                  icon: MessageSquare,
                  title: "Most people are using AI wrong",
                  desc: "Typing vague questions into ChatGPT and getting mediocre answers is not using AI. It is wasting time. The difference between a bad prompt and a great one is the difference between a useless output and one you can use immediately.",
                },
                {
                  icon: Target,
                  title: "The skill is learnable in hours, not months",
                  desc: "Unlike coding or data science, prompt engineering for business use can be learned in three focused sessions. The fundamentals are simple. The application is immediate. The results are visible from day one.",
                },
                {
                  icon: Brain,
                  title: "Leaders who do not adapt get bypassed",
                  desc: "Boards and investors are asking about AI strategy. Clients expect AI-augmented delivery. Leaders who cannot speak credibly about AI use cases in their own function are increasingly exposed.",
                },
                {
                  icon: Lightbulb,
                  title: "The tools are already in your stack",
                  desc: "Microsoft Copilot, Google Gemini, and ChatGPT are already available in most organizations. The bottleneck is not access to the tools. It is knowing how to use them to get business-grade outputs.",
                },
                {
                  icon: CheckCircle2,
                  title: "Three sessions is all it takes",
                  desc: "This is not a six-month course. It is a focused crash course that covers the 20% of AI skills that deliver 80% of the productivity gains for non-technical business professionals.",
                },
              ].map((item, i) => (
                <div key={i} className="border border-white/10 rounded-xl p-6">
                  <item.icon className="w-7 h-7 text-[#00D4AA] mb-4" />
                  <h3 className="font-semibold text-white mb-2">{item.title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
        {/* Audio Narrative - voice summary of this service page */}
        <div className="container max-w-4xl mx-auto px-4 py-6">
          <AudioPlayer
            text="AI Training for B2B Marketing and Sales Teams. Practical, hands-on AI training programs that equip your team to use Agentic AI, generative AI tools, and AI search optimization in their daily work. We deliver training that produces measurable output, not just awareness. From prompt engineering to AI workflow design, we build the capability your team needs to compete in an AI-first market."
            title="AI Training for B2B Marketing Teams"
            readingTime={5}
          />
        </div>

        {/* What You Learn */}
        <section className="container py-20">
          <h2 className="font-serif headline-lg mb-4">What you learn across three sessions</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mb-12">
            Each session builds on the last. By session three, you have a personal AI workflow
            that saves hours every week and a prompt library you can use immediately.
          </p>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                session: "Session 1",
                title: "How AI Actually Works",
                subtitle: "Foundation",
                color: "border-[#00D4AA]/30",
                accent: "text-[#00D4AA]",
                points: [
                  "What large language models are and are not",
                  "Why vague prompts produce vague outputs",
                  "The 5 core prompt structures for business tasks",
                  "How to give AI the right context, role, and format",
                  "Live exercises using your actual work tasks",
                ],
              },
              {
                session: "Session 2",
                title: "Prompting for Your Role",
                subtitle: "Application",
                color: "border-[#FF8C00]/30",
                accent: "text-[#FF8C00]",
                points: [
                  "Role-specific prompting: sales, marketing, ops, finance, leadership",
                  "Multi-step reasoning and chain-of-thought prompts",
                  "How to verify AI outputs and spot hallucinations",
                  "Building reusable prompt templates",
                  "AI for research, writing, analysis, and decision support",
                ],
              },
              {
                session: "Session 3",
                title: "Building Your AI Workflow",
                subtitle: "Compounding",
                color: "border-white/10",
                accent: "text-white",
                points: [
                  "Mapping AI into your daily and weekly task flow",
                  "Creating a personal prompt library for your role",
                  "How to stay current as AI tools evolve",
                  "AI for strategic thinking and scenario planning",
                  "What to delegate to AI vs. what to keep human",
                ],
              },
            ].map((s, i) => (
              <div key={i} className={`border ${s.color} rounded-xl p-7`}>
                <span className={`text-xs font-mono uppercase tracking-widest ${s.accent} mb-2 block`}>
                  {s.session}
                </span>
                <h3 className="font-serif text-xl font-normal mb-1">{s.title}</h3>
                <p className="text-sm text-muted-foreground mb-5">{s.subtitle}</p>
                <ul className="space-y-2">
                  {s.points.map((p, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${s.accent}`} />
                      {p}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>

        {/* Packages */}
        <section id="packages" className="bg-[#1a1a1a] text-white py-20">
          <div className="container">
            <h2 className="font-serif headline-lg mb-4">Packages and pricing</h2>
            <p className="text-gray-400 text-lg max-w-2xl mb-8">
              Choose the depth that matches your goals. All sessions are live, personalized,
              and built around your real work, not generic slides.
            </p>
            <div className="overflow-x-auto border border-white/10 rounded-xl mb-10">
              <table className="w-full min-w-[760px] text-sm">
                <thead className="bg-white/5"><tr><th className="px-4 py-3 text-left">Package</th><th className="px-4 py-3 text-left">Shape already on the page</th><th className="px-4 py-3 text-left">Price already on the page</th></tr></thead>
                <tbody className="divide-y divide-white/10 text-gray-300">
                  <tr><td className="px-4 py-4 text-white font-semibold">Starter, AI Foundations</td><td className="px-4 py-4">1 session, 90 minutes. Prompt starter kit. 30-day email Q&A.</td><td className="px-4 py-4">£495 per person</td></tr>
                  <tr><td className="px-4 py-4 text-white font-semibold">Accelerator, 3-Session Crash Course</td><td className="px-4 py-4">3 sessions, 90 minutes each. Everything in Starter. Marked most popular.</td><td className="px-4 py-4">£1,200 per person</td></tr>
                  <tr><td className="px-4 py-4 text-white font-semibold">Authority</td><td className="px-4 py-4">3 sessions plus a team workshop. Cohort of 4–12. Recorded library. Direct access to Modi for strategic AI questions.</td><td className="px-4 py-4">From £3,500 per cohort</td></tr>
                </tbody>
                <caption className="caption-bottom px-4 py-4 text-left text-gray-500">Most participants report saving 1 to 3 hours a day within the first week. The page also describes a 3 to 5x productivity advantage. That is not a contractual outcome.</caption>
              </table>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {packages.map((pkg, i) => (
                <div key={i} className={`border ${pkg.color} rounded-xl p-7 flex flex-col`}>
                  {pkg.badge && (
                    <span className="inline-block px-3 py-1 text-xs font-mono uppercase tracking-widest bg-[#FF8C00] text-white rounded-full mb-4 self-start">
                      {pkg.badge}
                    </span>
                  )}
                  <span className={`text-xs font-mono uppercase tracking-widest ${pkg.accent} mb-2 block`}>
                    {pkg.name}
                  </span>
                  <h3 className="font-serif text-2xl font-normal mb-1 text-white">{pkg.tagline}</h3>
                  <p className="text-sm text-gray-400 mb-4">{pkg.sessions}</p>
                  <div className="mb-6">
                    <span className={`text-3xl font-bold ${pkg.accent}`}>{pkg.price}</span>
                    <span className="text-sm text-gray-400 ml-2">{pkg.unit}</span>
                  </div>
                  <ul className="space-y-2 mb-8 flex-1">
                    {pkg.includes.map((item, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-gray-300">
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 flex-shrink-0 ${pkg.accent}`} />
                        {item}
                      </li>
                    ))}
                  </ul>
                  <a
                    href={pkg.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#FF8C00] text-white font-medium rounded-lg hover:bg-[#E07B00] transition-all duration-200 active:scale-[0.97] text-sm"
                  >
                    {pkg.cta} <ArrowRight className="w-4 h-4" />
                  </a>
                </div>
              ))}
            </div>
            <p className="text-gray-500 text-sm mt-8 text-center">
              All prices exclude VAT. Group discounts available for organizations booking 5 or more seats.
              <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London" href="https://calendly.com/modielnadi/30min" target="_blank" rel="noopener noreferrer" className="text-[#00D4AA] hover:underline ml-1" onClick={() => { trackCalendlyClick("service_ai_training"); trackSectionCtaClick({ section_name: "service_ai_training_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}>
                Contact us for a custom quote.
              </a>
            </p>
          </div>
        </section>

        {/* Who This Is For */}
        <section className="container py-20">
          <h2 className="font-serif headline-lg mb-4">Who this is for</h2>
          <p className="text-muted-foreground text-lg max-w-2xl mb-12">
            If you use a computer for work and you are not yet using AI tools daily, this training
            is for you. No exceptions.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
            {[
              { title: "B2B Leaders and Founders", desc: "CEOs, MDs, and founders who need to understand AI well enough to make strategic decisions, lead teams through change, and speak credibly to clients and investors." },
              { title: "Fintech and Startup Professionals", desc: "Product managers, growth leads, and commercial teams in fast-moving organizations where AI adoption is a competitive necessity, not a nice-to-have." },
              { title: "Marketing and Sales Teams", desc: "Professionals who write, research, present, and communicate for a living and want to do it faster, better, and at higher volume without burning out." },
              { title: "Operations and Finance Leaders", desc: "Heads of ops, CFOs, and analysts who want to use AI for data synthesis, reporting, scenario modeling, and process documentation without relying on technical teams." },
            ].map((item, i) => (
              <div key={i} className="border border-border rounded-xl p-6">
                <h3 className="font-semibold text-foreground mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          {/* Sectors We Train */}
          <div className="border-t border-border pt-16">
            <h2 className="font-serif text-3xl md:text-4xl mb-3">Sectors we train</h2>
            <p className="text-muted-foreground text-lg max-w-2xl mb-10">
              Every session is built around your sector's specific tools, workflows, and language.
              Generic training produces generic results - contextual training compounds.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  sector: "Fintech &amp; Financial Services",
                  badge: "Primary",
                  badgeColor: "bg-[#00D4AA]/15 text-[#00D4AA] border-[#00D4AA]/30",
                  points: [
                    "AI for credit analysis, risk narratives, and regulatory summaries",
                    "Prompting for investor updates, board packs, and compliance documentation",
                    "AI-assisted due diligence and market research workflows",
                    "Sector-specific tools: Copilot for Excel, Claude for long-form analysis",
                  ],
                },
                {
                  sector: "Tech Startups &amp; Scale-ups",
                  badge: "Primary",
                  badgeColor: "bg-[#00D4AA]/15 text-[#00D4AA] border-[#00D4AA]/30",
                  points: [
                    "AI for product specs, user research synthesis, and roadmap narratives",
                    "Prompting for pitch decks, investor memos, and fundraising materials",
                    "Agentic workflows for growth teams with limited headcount",
                    "How to build an AI-first culture before you hire your next 10 people",
                  ],
                },
                {
                  sector: "B2B Marketing &amp; Agentic Teams",
                  badge: "Primary",
                  badgeColor: "bg-[#00D4AA]/15 text-[#00D4AA] border-[#00D4AA]/30",
                  points: [
                    "AI for content briefs, campaign copy, and persona research",
                    "Prompting for AEO-optimized blog posts, LinkedIn, and email sequences",
                    "How to brief and QA agentic AI outputs without technical skills",
                    "Building a team prompt library that scales across campaigns",
                  ],
                },
                {
                  sector: "Professional Services",
                  badge: "Secondary",
                  badgeColor: "bg-white/5 text-muted-foreground border-border",
                  points: [
                    "AI for proposal writing, client reporting, and research synthesis",
                    "Prompting for presentations, executive summaries, and thought leadership",
                    "How to use AI to prepare for client meetings and strategic reviews",
                  ],
                },
                {
                  sector: "SaaS &amp; Enterprise Software",
                  badge: "Secondary",
                  badgeColor: "bg-white/5 text-muted-foreground border-border",
                  points: [
                    "AI for sales enablement, competitive intelligence, and RFP responses",
                    "Prompting for customer success playbooks and onboarding documentation",
                    "How to use AI to reduce time-to-value for enterprise deals",
                  ],
                },
                {
                  sector: "Legal &amp; Regulated Industries",
                  badge: "Emerging",
                  badgeColor: "bg-white/5 text-muted-foreground border-border",
                  points: [
                    "AI for document summarisation, research, and first-draft memos",
                    <>Understanding AI limitations in regulated contexts - what to delegate and what not to; see <Link href="/services/ai-governance/" className="text-[#00D4AA] hover:underline">governance</Link>.</>,
                    "Significant automation potential; covered at introductory level in current programs",
                  ],
                },
              ].map((item, i) => (
                <div key={i} className="border border-border rounded-xl p-6 flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-3">
                    <h3 className="font-semibold text-foreground text-base leading-snug" dangerouslySetInnerHTML={{ __html: item.sector }} />
                    <span className={`flex-shrink-0 text-xs font-mono px-2 py-0.5 rounded-full border ${item.badgeColor}`}>
                      {item.badge}
                    </span>
                  </div>
                  <ul className="space-y-2">
                    {item.points.map((pt, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <CheckCircle2 className="w-4 h-4 text-[#00D4AA] flex-shrink-0 mt-0.5" />
                        <span>{pt}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <p className="mt-8 text-sm text-muted-foreground max-w-2xl">
              Not sure if your sector is covered? Every session starts with a 15-minute intake
              conversation to map your role, tools, and goals before we begin.{" "}
              <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                href="https://calendly.com/modielnadi/30min?utm_source=website&utm_medium=service&utm_campaign=ai-training-sectors"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#00D4AA] hover:underline" onClick={() => { trackCalendlyClick("service_ai_training"); trackSectionCtaClick({ section_name: "service_ai_training_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
              >
                Book a free discovery call
              </a>{" "}
              to discuss your context.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-[#1a1a1a] text-white py-20">
          <div className="container max-w-3xl">
            <h2 className="font-serif headline-lg mb-12">Frequently asked questions</h2>
            <div className="space-y-3">
              <FaqAccordion
                  items={faqs}
                  className="space-y-3"
                  itemClassName="border border-white/10 rounded-xl overflow-hidden"
                  buttonClassName="w-full flex items-center justify-between p-5 text-left hover:bg-white/5 transition-colors"
                  questionClassName="m-0"
                  answerClassName="px-5 pb-5 text-gray-400 text-sm leading-relaxed"
                  iconClassName="w-5 h-5 text-[#00D4AA] shrink-0 transition-transform duration-200"
                />
            </div>
          </div>
        </section>

        {/* Related Services */}
        <section className="container py-16">
          <h2 className="font-serif text-2xl md:text-3xl mb-8">Related services</h2>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                title: "Agentic AI Lead Gen",
                desc: "Once your team knows how to use AI, deploy multi-agent systems that run your lead generation 24/7.",
                href: "/services/gemini-agentic-ai/",
              },
              {
                title: "AI Governance and Compliance",
                desc: "Build the policies and guardrails that keep your AI use safe, compliant, and defensible.",
                href: "/services/ai-governance/",
              },
              {
                title: "Persona-Driven GTM Strategy",
                desc: "Apply your new AI skills to a full go-to-market strategy built around your ideal customer profiles.",
                href: "/services/ai-marketing-strategy/",
              },
            ].map((s, i) => (
              <Link
                key={i}
                href={s.href}
                className="border border-border rounded-xl p-6 hover:border-[#00D4AA]/50 transition-colors group"
              >
                <h3 className="font-semibold text-foreground mb-2 group-hover:text-[#00D4AA] transition-colors">
                  {s.title}
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
                <span className="inline-flex items-center gap-1 text-xs text-[#00D4AA] mt-3">
                  Learn more <ArrowRight className="w-3 h-3" />
                </span>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA Strip */}
        <section className="bg-[#FF8C00] py-16">
          <div className="container text-center">
            <h2 className="font-serif text-3xl md:text-5xl text-white mb-4">
              Book your first session today
            </h2>
            <p className="text-white/80 text-lg max-w-xl mx-auto mb-8">
              Three sessions. Real skills. A 3 to 5x productivity advantage over colleagues who
              are still figuring out what prompt engineering means.
            </p>
            <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
              href="https://calendly.com/modielnadi/30min?utm_source=website&utm_medium=service&utm_campaign=ai-training-cta"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#FF8C00] font-semibold rounded-lg hover:bg-white/90 transition-all duration-200 active:scale-[0.97] text-lg" onClick={() => { trackCalendlyClick("service_ai_training"); trackSectionCtaClick({ section_name: "service_ai_training_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
            >
              Book a discovery call <ArrowRight className="w-5 h-5" />
            </a>
          </div>
        </section>

        <section className="mx-auto max-w-5xl px-4 pb-12" aria-label="Free AI Visibility Score"><ScoreInlineForm placement="service_end" /></section>
      <Footer />

        {/* Final CTA - three ways to work with us */}
        <section id="get-started" className="py-20 lg:py-28 bg-[#0F1419]" aria-labelledby="service-final-cta-heading">
          <div className="container">
            <div className="text-center mb-14">
              <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest block mb-4">Ready to start?</span>
              <h2 id="service-final-cta-heading" className="font-serif text-3xl md:text-4xl text-white mb-4">
                Three ways to work with us
              </h2>
              <p className="text-white/50 text-lg max-w-xl mx-auto">
                Pick the path that fits where you are right now.
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">Free · No obligation</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Audit your domain for AI Answers</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  See exactly where your brand appears in Google AI Mode, ChatGPT, Perplexity, and Gemini. Free AI Visibility Score with a scored report and gap analysis.
                </p>
                <a aria-label="Check my AI visibility"
                  href="/free-ai-growth-audit/"
                  className="inline-flex items-center justify-center gap-2 bg-[#FF8C00] text-[#0F1419] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#E07B00] transition-colors active:scale-[0.97]" onClick={() => trackSectionCtaClick({ section_name: "service_ai_training_bottom_cta", cta_text: "Check my AI visibility", destination: "/free-ai-growth-audit/" })}
                >
                  Check my AI visibility<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">1-to-1 · Personalised</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">AI Executive Training for Leaders</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  Three sessions built around your role, your sector, and your goals. Hands-on AI training that makes you dangerous with the tools your competitors are still figuring out.
                </p>
                <a
                  href="/services/ai-training-for-leaders/"
                  className="inline-flex items-center justify-center gap-2 border border-[#FF8C00] text-[#FF8C00] px-6 py-3.5 text-sm font-bold rounded-sm hover:bg-[#FF8C00] hover:text-[#0F1419] transition-colors active:scale-[0.97]"
                >
                  Explore AI Training
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
              <div className="flex flex-col bg-white/5 border border-white/10 rounded-sm p-8 hover:border-[#FF8C00]/40 transition-colors duration-200">
                <span className="font-mono text-[#FF8C00] text-xs uppercase tracking-widest mb-4">30 min · No pitch</span>
                <h3 className="font-serif text-xl text-white mb-3 leading-snug">Discuss your AI marketing strategy</h3>
                <p className="text-white/50 text-sm leading-relaxed flex-1 mb-6">
                  A focused conversation about where you are, where you want to be, and what a realistic 90-day AI marketing deployment looks like for your business. No deck. No sales script.
                </p>
                <a aria-label="Book a free AI marketing strategy call with Modi Elnadi, Integrated.Social London"
                  href="https://calendly.com/modielnadi/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 border border-white/20 text-white/80 px-6 py-3.5 text-sm font-bold rounded-sm hover:border-[#FF8C00] hover:text-[#FF8C00] transition-colors active:scale-[0.97]" onClick={() => { trackCalendlyClick("service_ai_training"); trackSectionCtaClick({ section_name: "service_ai_training_hero", cta_text: "Book a free strategy call", destination: "https://calendly.com/modielnadi/30min" }); }}
                >
                  Schedule a discovery call
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
                </a>
              </div>
            </div>
          </div>
        </section>
        </main>
      </div>
      {/* Arabic-aware share row */}
      <div className="container max-w-5xl mx-auto px-4 pb-16">
        <SharePostRow
          title="AI Training for Leaders"
          canonicalUrl="https://integrated.social/services/ai-training-for-leaders/"
          excerpt="Hands-on AI training programs that turn senior teams into confident AI practitioners."
        />
      </div>

      {/* Arabic translation toggle */}
      <ArabicToggle />
    </>
  );
}
